/**
 * Storage and public surface of the property validators the strict TSCN parser runs. What it resolves to lives beside
 * it and is re-exported here: `propertyValidator.ts` (what a validator declares), `wildcardIndex.ts` (the two wildcard key
 * shapes Godot writes) and `unavailableKey.ts` (a key a type takes away from its base). The resolution walk is in
 * `validatorResolution.ts`, and the removal rules are in `removalKeys.ts`.
 */
import { CLASS_BASE_TYPES } from '../godot/classdb/classBaseTypes.js';
import { buildWildcardIndex } from './wildcardIndex.js';
import { resolveDeclaration } from './validatorResolution.js';
import { refuseOverlap, unavailableKeysOf } from './removalKeys.js';
/**
 * Throws on a key `keys` repeats, or one `own` already holds. Checked before anything is stored, so a
 * refused call registers nothing.
 */
function refuseRedeclaration(nodeType, keys, own) {
    const declared = new Set(Object.keys(own));
    for (const key of keys) {
        if (declared.has(key)) {
            throw new Error(`${nodeType} already declares '${key}'; a type declares each key once`);
        }
        declared.add(key);
    }
}
/**
 * Registry for property validators by node type
 */
export class ValidatorRegistry {
    baseTypes;
    validators = new Map();
    unavailable = new Map();
    /** Wildcard patterns per type, prefixes pre-sliced. Rebuilt on every registerAll. */
    wildcards = new Map();
    /** The three maps and the base lookup, built once: the walks run per property and allocate nothing. */
    tables = {
        validators: this.validators,
        unavailable: this.unavailable,
        wildcards: this.wildcards,
        baseOf: (type) => this.baseOf(type),
    };
    /**
     * @param baseTypes - class → base-type map driving the inheritance walk in
     *   `findValidator` (see classBaseTypes.ts). Defaults to `{}` (no inheritance,
     *   pure exact/wildcard matching); the shared singleton wires the real one.
     */
    constructor(baseTypes = {}) {
        this.baseTypes = baseTypes;
    }
    /**
     * The declared base of `type`, or `undefined`.
     *
     * `Object.hasOwn`, because the key is a node type the `.tscn` chooses: bare
     * indexing answers the `Object` function for `type="constructor"`, which then
     * travels as a `string` through every walk below.
     */
    baseOf(type) {
        return Object.hasOwn(this.baseTypes, type) ? this.baseTypes[type] : undefined;
    }
    /**
     * Register the validators of a node type, from one table or several parts of one. A key belongs to
     * one part: a key that an earlier part, or an earlier call for the same type, declares throws, where
     * a merge would keep the later validator and drop the earlier one without a report.
     * @param nodeType - TSCN node type (for example "MeshInstance3D")
     * @param groups - one or more maps of property keys to validator functions
     */
    registerAll(nodeType, ...groups) {
        const keys = groups.flatMap((group) => Object.keys(group));
        refuseOverlap(nodeType, keys, this.unavailable, 'removes');
        const own = this.validators.get(nodeType) ?? {};
        refuseRedeclaration(nodeType, keys, own);
        this.validators.set(nodeType, own);
        for (const group of groups)
            Object.assign(own, group);
        this.wildcards.set(nodeType, buildWildcardIndex(own));
    }
    /**
     * Declare that `nodeType` removes properties its base chain declares. `removalKeys.ts` says what makes a key a
     * removal rather than an inert one.
     *
     * @param nodeType - the concrete type that cannot carry the properties.
     * @param removals - property key → `{ reason, cite }`: `reason` is the diagnostic's text, and `cite` is the
     *   `file:line` of the guard that refuses the write (ADR-0032).
     */
    registerUnavailable(nodeType, removals) {
        refuseOverlap(nodeType, Object.keys(removals), this.validators, 'declares');
        if (!this.unavailable.has(nodeType)) {
            this.unavailable.set(nodeType, {});
        }
        Object.assign(this.unavailable.get(nodeType), removals);
    }
    /**
     * Every type that declares a removal, with the keys it removes directly.
     *
     * @internal Like {@link ValidatorRegistry.typesWithRegistrations}, this lists the registry without naming a type,
     * which is the job of `linter/registryPopulation.ts` and `linter/testing/registryPopulation.ts` alone. Types
     * declaring a removal are not a subset of `registeredTypes('declaring')`: `HBoxContainer` only takes `vertical`
     * away and registers no validator, so a scan of the validator map misses it.
     */
    removedKeysByType() {
        return new Map([...this.unavailable].map(([nodeType, removals]) => [nodeType, Object.keys(removals)]));
    }
    /**
     * Keys `nodeType` removes, declared here or inherited, resolved as `findValidator` resolves them so a sheet's
     * "unavailable" list never holds a key the linter accepts: a removal wins at the hop that declares it, and a nearer
     * type re-declaring the key takes it back.
     */
    getUnavailableKeys(nodeType) {
        return unavailableKeysOf(this.tables, nodeType);
    }
    /**
     * The validator for a property, the owner type first and then each base in
     * turn (`validatorResolution.ts`).
     *
     * @returns Something to call, or null if neither the type nor its bases match.
     *   Deliberately untagged: see {@link ValidatorFn}. A caller introspecting a
     *   declaration asks {@link ValidatorRegistry.declarationFor} instead.
     */
    declarationFor(nodeType, propertyKey) {
        return resolveDeclaration(this.tables, nodeType, propertyKey);
    }
    /**
     * The same resolution, as something to call. A `PropertyValidator` is a `ValidatorFn` with tags, so the narrowing needs
     * no assertion. Running a validator needs no tags, and reading one is introspection for a guard: keeping them apart at
     * the type level stops a hand-assembled roots-only population, since `findValidator(...).intSlot` does not type-check.
     */
    findValidator(nodeType, propertyKey) {
        return this.declarationFor(nodeType, propertyKey);
    }
    /**
     * Node types that have validators registered.
     *
     * @internal `linter/registryPopulation.ts` and `linter/testing/registryPopulation.ts` are the only callers: listing
     * the registry without naming a type is their job alone, so no guard assembles the roots-only population by hand.
     */
    typesWithRegistrations() {
        return [...this.validators.keys()];
    }
    /**
     * Keys registered directly under `nodeType` (own registration only, no
     * base-walk). Used by the meta-guard test to detect shadow copies.
     */
    getOwnKeys(nodeType) {
        const own = this.validators.get(nodeType);
        return own ? Object.keys(own) : [];
    }
    /**
     * Clear all registered validators (useful for testing)
     */
    clear() {
        this.validators.clear();
        this.unavailable.clear();
        this.wildcards.clear();
    }
}
/**
 * The ValidatorRegistry, wired with Godot's real node and resource ancestry (classBaseTypes.ts), so every subclass
 * inherits its base validators.
 */
export const validatorRegistry = new ValidatorRegistry(CLASS_BASE_TYPES);
//# sourceMappingURL=ValidatorRegistry.js.map